/**
 * Apresentação da LISTAGEM do catálogo (Entrega 3, item 3.1) — busca, filtros e
 * paginação CLIENT-SIDE sobre o MESMO `dataset.products`. Nada aqui recalcula
 * regra de catálogo nem toca em `/api/*`: é só recorte de apresentação da lista
 * já carregada (disponibilidade/preço/divergência continuam vindo dos módulos
 * puros de `catalog/*`). Puro e sem I/O, para ser verificável sem navegador.
 *
 * O estado (busca/filtros/página) é lido da URL pela tela (`useSearchParams`),
 * então a listagem é deep-linkável e navegável — recarregar mantém o recorte.
 */
import { availabilityOfProduct } from "./availability.js";
import type { PlatformCatalogProduct } from "../api/types.js";

/** Tamanho de página fixo da listagem (apresentação; não é regra de negócio). */
export const CATALOG_PAGE_SIZE = 10;

/** Disponibilidade consolidada em três faixas de apresentação (ícone+texto). */
export type AvailabilityBucket = "disponivel" | "critico" | "indisponivel";

export interface CatalogListFilters {
  /** Texto livre: casa SKU, nome comercial, nome, categoria e fornecedor. */
  search: string;
  /** Categoria operacional exata; `null` = todas. */
  category: string | null;
  /** Fornecedor operacional exato; `null` = todos. */
  supplier: string | null;
  /** Publicação na loja; `null` = ambos. */
  published: boolean | null;
  /** Faixa de disponibilidade consolidada; `null` = todas. */
  availability: AvailabilityBucket | null;
  /** Página 1-based. */
  page: number;
}

export const EMPTY_FILTERS: CatalogListFilters = {
  search: "",
  category: null,
  supplier: null,
  published: null,
  availability: null,
  page: 1
};

/** Faixa de disponibilidade de um produto (consolidada, nunca estoque bruto). */
export function availabilityBucketOf(product: PlatformCatalogProduct): AvailabilityBucket {
  const availability = availabilityOfProduct(product);
  // Sem oferta vendável OU disponibilidade zerada = indisponível (estado
  // próprio, distinto de "crítico": ver availability.ts).
  if (availability.sellableStock === null || availability.consolidated === 0) return "indisponivel";
  if (availability.critical) return "critico";
  return "disponivel";
}

/** Opções distintas de uma chave operacional, ordenadas, para o <select>. */
function distinctOf(products: PlatformCatalogProduct[], pick: (p: PlatformCatalogProduct) => string | null): string[] {
  const set = new Set<string>();
  for (const product of products) {
    const value = pick(product);
    if (value) set.add(value);
  }
  return [...set].sort((a, b) => a.localeCompare(b, "pt-BR"));
}

export function categoryOptions(products: PlatformCatalogProduct[]): string[] {
  return distinctOf(products, (p) => p.operational.category);
}

export function supplierOptions(products: PlatformCatalogProduct[]): string[] {
  return distinctOf(products, (p) => p.operational.supplier);
}

function matchesSearch(product: PlatformCatalogProduct, term: string): boolean {
  if (!term) return true;
  const needle = term.trim().toLowerCase();
  if (!needle) return true;
  const haystack = [
    product.operational.sku,
    product.commercial.commercialName ?? "",
    product.name,
    product.operational.category ?? "",
    product.operational.supplier ?? ""
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(needle);
}

/** Aplica os filtros (sem paginar). Ordem estável = ordem do dataset. */
export function filterCatalogProducts(
  products: PlatformCatalogProduct[],
  filters: CatalogListFilters
): PlatformCatalogProduct[] {
  return products.filter((product) => {
    if (!matchesSearch(product, filters.search)) return false;
    if (filters.category !== null && product.operational.category !== filters.category) return false;
    if (filters.supplier !== null && product.operational.supplier !== filters.supplier) return false;
    if (filters.published !== null && product.commercial.published !== filters.published) return false;
    if (filters.availability !== null && availabilityBucketOf(product) !== filters.availability) return false;
    return true;
  });
}

export interface CatalogPage {
  /** Linhas da página corrente. */
  rows: PlatformCatalogProduct[];
  /** Total após filtro (antes de paginar). */
  total: number;
  /** Página corrente, saneada para o intervalo [1, totalPages]. */
  page: number;
  totalPages: number;
  /** Índice 1-based do primeiro/último item exibido (0/0 quando vazio). */
  from: number;
  to: number;
  pageSize: number;
}

/**
 * Filtra e pagina. A página é saneada: pedir uma página fora do intervalo cai
 * na última válida (nunca uma tela vazia por `?page=999`).
 */
export function paginateCatalog(
  products: PlatformCatalogProduct[],
  filters: CatalogListFilters,
  pageSize: number = CATALOG_PAGE_SIZE
): CatalogPage {
  const filtered = filterCatalogProducts(products, filters);
  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const page = Math.min(Math.max(1, filters.page), totalPages);
  const start = (page - 1) * pageSize;
  const rows = filtered.slice(start, start + pageSize);
  return {
    rows,
    total,
    page,
    totalPages,
    from: total === 0 ? 0 : start + 1,
    to: total === 0 ? 0 : start + rows.length,
    pageSize
  };
}

/** `true` quando algum filtro/busca está ativo (muda o texto do empty state). */
export function hasActiveFilters(filters: CatalogListFilters): boolean {
  return Boolean(
    filters.search.trim() ||
      filters.category !== null ||
      filters.supplier !== null ||
      filters.published !== null ||
      filters.availability !== null
  );
}

/** Lê os filtros de um `URLSearchParams` (ou equivalente). Valores inválidos caem no default. */
export function filtersFromParams(params: {
  get(name: string): string | null;
}): CatalogListFilters {
  const published = params.get("publicado");
  const availability = params.get("disponibilidade");
  const pageRaw = Number.parseInt(params.get("pagina") ?? "", 10);
  const availabilityBuckets: AvailabilityBucket[] = ["disponivel", "critico", "indisponivel"];
  return {
    search: params.get("busca") ?? "",
    category: params.get("categoria") || null,
    supplier: params.get("fornecedor") || null,
    published: published === "sim" ? true : published === "nao" ? false : null,
    availability: availabilityBuckets.includes(availability as AvailabilityBucket)
      ? (availability as AvailabilityBucket)
      : null,
    page: Number.isFinite(pageRaw) && pageRaw > 0 ? pageRaw : 1
  };
}
